<script setup>
import { Head, Link } from '@inertiajs/vue3';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import { defineProps, nextTick, ref } from 'vue';
import Footer from '@/Components/Footer.vue';
import PublicLayout from '@/Layouts/PublicLayout.vue';

import CarSVG from '../../images/misc/car.svg';
import KilnSVG from '../../images/misc/kiln.svg';
import CementSVG from '../../images/misc/cement.svg';
import FieldSVG from '../../images/misc/field.svg';

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
});

let slogan_display = ref([]);
let slogan_c = 0;
let slogan_animate = ref(true);
let duration = 3000;

const slogans = [
    'Cars are fantastic.',
    'Carbon is not.',
    'Combustion is exciting.',
    'Emissions aren\'t.',
    'We love our vehicles.',
    'Let\'s keep them.',
];
slogans.push(slogans[0]);

for (let i = 0; i < slogans.length; i++) {
    slogan_display.value.push(i);
    slogan_display.value[i] = true;
}

const advance_slogan = () => {
    slogan_display.value[slogan_c] = false;
    slogan_c++;
    if (slogan_c >= slogans.length-1) {
        setTimeout(async () => {
            slogan_animate.value = false;
            await nextTick();
            slogan_c = 0;
            for (let i = 0; i < slogans.length; i++) {
                slogan_display.value[i] = true;
            }
            setTimeout(() => {
                slogan_animate.value = true;
            }, 100);
        }, duration/2);
    }
}


// const jimg_sizes = [1920, 960, 640, 480, 384];
const jimg_sizes = [384, 480, 640, 960, 1920];
const jimgs = ref([]);
for (let i = 0; i < 20; i++) {
    jimgs.value.push(false);
}
let jimg_c = 0;
jimgs.value[jimg_c] = true;

const advance_jimg = () => {
    jimgs.value[jimg_c] = false;
    jimg_c++;
    if (jimg_c >= jimgs.value.length) {
        jimg_c = 0;
    }
    jimgs.value[jimg_c] = true;
}    

setInterval(async () => {
    advance_slogan();
    advance_jimg();
}, duration);

</script>

<style>
@keyframes carbon-build {
    0% { transform: rotate(-45deg); opacity: 0.4; }
    30% { opacity: 0.4; }
    35% { opacity: 1; }
    60% { opacity: 1; }
    65% { opacity: 0; }
    100% { transform: rotate(225deg); opacity: 0; }
}
.ani-cb {
    transform: rotate(-45deg);
    animation: carbon-build 12s infinite;
    animation-timing-function: linear;
}

@keyframes carbon-agri {
    0% { transform: rotate(-45deg); opacity: 0.4; }
    30% { opacity: 0.4; }
    35% { opacity: 1; }
    92% { opacity: 1; }
    98% { opacity: 0; }
    100% { transform: rotate(225deg); opacity: 0; }
}
.ani-ag {
    transform: rotate(-45deg);
    animation: carbon-agri 12s infinite;
    animation-timing-function: linear;
}

.ani-cb:nth-child(1) {
    animation-delay: 0s;
}
.ani-ag:nth-child(2) {
    animation-delay: 1s;
}
.ani-cb:nth-child(3) {
    animation-delay: 2s;
}
.ani-ag:nth-child(4) {
    animation-delay: 3s;
}
.ani-cb:nth-child(5) {
    animation-delay: 4s;
}
.ani-ag:nth-child(6) {
    animation-delay: 5s;
}
.ani-cb:nth-child(7) {
    animation-delay: 6s;
}
.ani-ag:nth-child(8) {
    animation-delay: 7s;
}
.ani-cb:nth-child(9) {
    animation-delay: 8s;
}
.ani-ag:nth-child(10) {
    animation-delay: 9s;
}
.ani-cb:nth-child(11) {
    animation-delay: 10s;
}
.ani-ag:nth-child(12) {
    animation-delay: 11s;
}
</style>

<template>
    <Head title="Welcome" />

    <PublicLayout :route="route" :canLogin="canLogin">    
        <div class="text-neutral-800 mt-20 h-16 sm:h-24 w-full relative">
            <div v-for="(slogan, index) in slogans" class="text-2xl sm:text-5xl inline-block duration-500 absolute left-1/2 -translate-x-1/2 top-0 text-center h-full text-nowrap"
            :class="{ 'bg-gradient-to-br from-ipsum-teal-700 to-ipsum-green-400 text-transparent bg-clip-text': (index % 2) == 0, 'transition-none': !slogan_animate, 'opacity-0': index != slogan_c }">
                {{ slogan }}
            </div>
        </div>
        
        <div class="w-full">
            <picture v-for="(jimg, index) in jimgs" class="w-full h-auto transition-all duration-500" :class="{ 'opacity-0': !jimg }">
                <source v-for="size in jimg_sizes" :srcset="`/i/jimgs/optimised/${index}-${size}.webp`" type="image/webp" :media="`(max-width: ${size}px)`">
                <source v-for="size in jimg_sizes" :srcset="`/i/jimgs/optimised/${index}-${size}.jpg`" type="image/jpeg" :media="`(max-width: ${size}px)`">
                <img :src="`/i/jimgs/optimised/${index}-1920.jpg`" alt="A car" class="mx-auto absolute w-full max-w-7xl h-auto xl:rounded-lg xl:shadow-lg" loading="lazy" />
            </picture>

            <img :src="`/i/jimgs/optimised/0-480.jpg`" alt="A car" class="mx-auto w-full max-w-7xl h-auto opacity-0" />
        </div>
            
        <div class="w-full mt-20 px-4 text-center">
            <p class="mt-2 text-2xl lg:text-4xl">
                The car you already have and love, <span class="bg-gradient-to-br from-ipsum-teal-700 to-ipsum-green-400 text-transparent bg-clip-text font-bold inline-block">but with net-zero CO<span class="text-xl lg:text-3xl">2</span>.</span>
            </p>
            <Link :href="route('register')" class="my-6 block lg:my-12">
                <PrimaryButton class="!text-base lg:!text-2xl">
                    Register
                </PrimaryButton>
            </Link>
            <p class="text-base max-w-2xl mx-auto lg:text-xl">
                Switching may feel inevitable, but signing up to <span class="font-bold">Turncarbon</span> lets you stop worrying about emissions while keeping your existing car.
            </p>
            <svg class="h-8 w-auto mx-auto block mt-8 lg:h-12 lg:mt-16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30"><clipPath id="a"><path d="M0 0v30h60V0z"/></clipPath><clipPath id="b"><path d="M30 15h30v15zv15H0zH0V0zV0h30z"/></clipPath><g clip-path="url(#a)"><path fill="#012169" d="M0 0v30h60V0z"/><path stroke="#fff" stroke-width="6" d="m0 0 60 30m0-30L0 30"/><path stroke="#C8102E" stroke-width="4" d="m0 0 60 30m0-30L0 30" clip-path="url(#b)"/><path stroke="#fff" stroke-width="10" d="M30 0v30M0 15h60"/><path stroke="#C8102E" stroke-width="6" d="M30 0v30M0 15h60"/></g></svg>
            <h1 class="uppercase tracking-widest text-2xl font-bold mt-3 lg:text-4xl lg:mt-6">Welcome to Turncarbon</h1>
            <h2 class="text-base lg:text-xl lg:mt-2">The UK's first carbon removal service for motorists.</h2>
        </div>

        <div class="text-left text-sm lg:text-lg bg-neutral-100 py-4 px-6 my-20 w-full shadow-lg border-y border-neutral-200 mx-auto max-w-4xl md:border md:rounded-lg">
            <p class="uppercase tracking-widest font-bold text-lg mb-2">How it works</p>
            <ul class="">
                <li class="mt-6 pl-12 relative min-h-8 lg:min-h-10">
                    <span class="text-neutral-400 text-2xl lg:text-4xl mr-1 absolute left-0">1.</span>
                    <Link :href="route('register')">
                        <PrimaryButton class="!px-2 !py-1 mr-1 lg:text-lg">
                            Register
                        </PrimaryButton>
                    </Link> your free account and securely add your One-off payment method.
                    <br />
                    <span class="text-neutral-500 text-xs lg:text-base">
                        We've partnered with a top UK open banking provider so that know your data and money are safe.
                    </span>
                </li>
                <li class="mt-4 pl-12 relative min-h-8 lg:min-h-10">
                    <span class="text-neutral-400 text-2xl lg:text-4xl mr-1 absolute left-0">2.</span> Pay for fuel at the pumps like normal.
                    <br />
                    <span class="text-neutral-500 text-xs lg:text-base">
                        Turncarbon calculates the carbon emissions from your purchase.
                    </span>
                </li>
                <li class="mt-4 pl-12 relative min-h-8 lg:min-h-10">
                    <span class="text-neutral-400 text-2xl lg:text-4xl mr-1 absolute left-0">3.</span> With one click, remove the same amount of carbon from the atmosphere.
                    <span class="bg-gradient-to-br from-ipsum-teal-700 to-ipsum-green-400 text-transparent bg-clip-text inline-block font-bold">Enjoy net-zero motoring.</span>
                </li>
            </ul>
        </div>

        <div class="w-full my-24 lg:my-32">
            <div class="bg-gradient-to-br from-ipsum-teal-500 to-ipsum-green-400 w-72 h-72 lg:w-96 lg:h-96 lg:p-8 rounded-full mx-auto p-4 relative fill-neutral-700 stroke-neutral-700">
                <div v-for="index in 12" class="absolute w-2 h-40 lg:h-56 bottom-1/2 left-1/2 -translate-x-1/2 origin-bottom" :class="{ 'ani-ag': index % 2 == 0 , 'ani-cb': index % 2 == 1 }">
                    <div class="rounded-full bg-neutral-800 w-2 h-2 lg:w-3 lg:h-3"></div>
                </div>

                <div class="bg-neutral-50 w-64 h-64 lg:w-80 lg:h-80 rounded-full mx-auto relative">
                    <div class="w-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-neutral-600 text-sm lg:text-base pt-8">
                        Carbon removed for at least
                        <span class="uppercase tracking-widest bg-gradient-to-br from-ipsum-teal-700 to-ipsum-green-400 text-transparent bg-clip-text inline-block font-bold text-4xl lg:text-5xl">100 years</span>
                        <span class="mt-2 lg:mt-3 block">
                            powered by
                        </span>
                        <img src="/i/carbonfuture-min.png" alt="Carbon Future" class="w-36 lg:w-40 inline-block h-auto mx-auto block" />
                    </div>
                </div>

                <CarSVG class="h-20 w-24 absolute -left-4 -top-2"/>
                <KilnSVG class="h-14 w-14 absolute -right-1 -top-1"/>
                <CementSVG class="h-14 w-14 absolute -right-1 -bottom-1"/>
                <FieldSVG class="h-14 w-14 absolute -left-1 -bottom-1"/>
            </div>
        </div>
    </PublicLayout>
</template>
